import { useMutation } from '@tanstack/react-query';
import { updateCurrentUserMutation } from '@/sdk/@tanstack/react-query.gen';
import { captureEvent, captureFailure } from '@/posthog-analytics/posthog';
import { useAuth } from '../useAuth';
import type { NameCaptureVariant } from './NameCaptureForm';
import type { PrefillSource } from './profileCompletion';

interface UseUpdateMyNameOptions {
  surface: NameCaptureVariant;
  /** What was in the inputs before the person touched them, so we can tell
   *  whether they accepted our guess or replaced it. If `edited_suggestion`
   *  turns out to be near zero, people are rubber-stamping a name we made up
   *  off their email and we should stop asking this way. */
  prefilled: { first: string; last: string; source: PrefillSource };
  onSaved?: () => void;
}

/** Saves the name, then refreshes the auth session.
 *
 *  The refresh is not optional: `AuthContext` reads the allauth session payload,
 *  not this response, so without it the gate would still see a blank name and
 *  re-fire on the next navigation.
 */
export function useUpdateMyName({ surface, prefilled, onSaved }: UseUpdateMyNameOptions) {
  const { refreshAuth } = useAuth();
  const analytics = { surface, prefill_source: prefilled.source };

  return useMutation({
    ...updateCurrentUserMutation(),
    onMutate: (variables) => {
      const body = variables.body;
      captureEvent('profile_name_submitted', {
        ...analytics,
        edited_suggestion:
          body?.first_name !== prefilled.first || (body?.last_name ?? '') !== prefilled.last,
      });
    },
    onSuccess: async () => {
      captureEvent('profile_name_saved', analytics);
      await refreshAuth();
      onSaved?.();
    },
    onError: (error) => {
      captureFailure('profile_name_save_failed', error, analytics);
    },
  });
}
